/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { KeyboardEvent, useEffect, useRef, useState } from "react";

import { IDict } from "../other/constants";
import { CustomButton } from "./CustomButton";
import { CustomInputLocation, CustomInputUnit } from "./CustomInput";
import { ControlledCustomSelect } from "./CustomSelect";
import { AppIconButton } from "../buttons/AppIconButton";
import { Search } from "lucide-react";
import { APIProvider } from "@vis.gl/react-google-maps";
import ContentLayout from "../layouts/ContentLayout";
import { DISTANCE, PRICE, UNITS } from "./constants";
import { IFilters } from "./ShowroomSearchResults";

let addressInterval: any;

interface ISearchResultsHeader {
  dict: IDict;
  filters: IFilters;
  setFilters: (filters: any) => void;
  activeBtn: string;
  selectedPlace: any;
  setSelectedPlace: (active: any) => void;
  setActiveBtn: (active: string) => void;
  handleApplyFilters: () => void;
  loading: boolean;
}

const SearchResultsHeader = ({
  dict,
  filters,
  setFilters,
  activeBtn,
  setActiveBtn,
  setSelectedPlace,
  selectedPlace,
  handleApplyFilters,
  loading,
}: ISearchResultsHeader) => {
  const [clearAddressInput, setClearAddressInput] = useState(0);
  const errorMessageShown = useRef(false);

  // ! when google places changes, pull name and coordinates from it
  useEffect(() => {
    clearInterval(addressInterval);

    if (!selectedPlace) return;

    if (selectedPlace?.name && Object.keys(selectedPlace).length === 1) {
      setClearAddressInput((prev) => prev + 1);
    } else {
      const tmp = { ...filters };
      if (selectedPlace.fromBrowser) {
        const lat = selectedPlace.coords?.lat;
        const lng = selectedPlace.coords?.lng;
        const name = selectedPlace.formatted_address;
        const coordinates = `${lat},${lng}`;

        tmp.name = name;
        tmp.coordinates = coordinates;
      } else {
        const lat = selectedPlace.geometry?.location?.lat();
        const lng = selectedPlace.geometry?.location?.lng();
        const name = selectedPlace.formatted_address;
        const coordinates = `${lat},${lng}`;

        tmp.name = name;
        tmp.coordinates = coordinates;
      }

      if (!filters.distance) {
        tmp.distance = "within50miles";
      }

      setFilters(tmp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlace]);

  // ! google places change
  const handleLocationOnChange = () => {
    setSelectedPlace(null);
    errorMessageShown.current = false;
  };

  // ! first tab filters
  const handleFilterChange = (value: string, name: string) => {
    const tmp: IFilters = { ...filters };
    (tmp as any)[name] = value;

    setFilters(tmp);
  };

  // ! unitId input
  const handleInputChange = (value: any) => {
    const tmp: IFilters = { ...filters };

    tmp.unitId = value || "";
    setFilters(tmp);
  };

  const handleAddressBlur = () => {
    addressInterval = setTimeout(() => {
      checkAfterBlur();
    }, 500);
  };

  const checkAfterBlur = () => {
    if (!selectedPlace) {
      if (!errorMessageShown.current) {
        errorMessageShown.current = true;

        setClearAddressInput((prev) => prev + 1);
      }
    }
  };

  const handleUnitIdKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && filters.unitId) {
      handleApplyFilters();
    }
  };

  return (
    <ContentLayout bgColorClass="bg-royal-blue" innerClass="sm:pt-[48px] pt-[48px]">
      <div className="flex flex-col w-full">
        <div className="lg:flex-row lg:gap-6 flex flex-col items-center gap-4">
          <CustomButton
            label={dict.searchByLocation}
            setActiveBtn={() => setActiveBtn("location")}
            active={activeBtn === "location"}
            disabled={loading}
          />
          <CustomButton
            label={dict.searchByUnit}
            setActiveBtn={() => setActiveBtn("unit")}
            active={activeBtn === "unit"}
            disabled={loading}
          />
        </div>

        {activeBtn === "location" ? (
          <div className="lg:gap-10 lg:mt-10 flex flex-col gap-6 mt-6 mb-10">
            <APIProvider apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API!}>
              <CustomInputLocation
                placeholder={dict.locationInputPlaceholder}
                setSelectedPlace={setSelectedPlace}
                handleLocationOnChange={handleLocationOnChange}
                name={filters.name}
                disabled={loading}
                clearAddressInput={clearAddressInput}
                handleAddressBlur={handleAddressBlur}
                useMyLocationTooltip={dict.useMyLocation}
              />

              <div className="lg:flex-row gap-x-6 gap-y-4 flex flex-col">
                <ControlledCustomSelect
                  title="distance dropdown"
                  key="distance"
                  dict={dict}
                  value={filters.distance}
                  data={DISTANCE}
                  onChange={handleFilterChange}
                  name="distance"
                  placeholder="distanceMiles"
                  darkBg
                  disabled={loading}
                />
                <ControlledCustomSelect
                  title="unit dropdown"
                  key="unitType"
                  dict={dict}
                  value={filters.unitType}
                  data={UNITS}
                  onChange={handleFilterChange}
                  name="unitType"
                  placeholder="unitType"
                  darkBg
                  disabled={loading}
                />
                <ControlledCustomSelect
                  title="price dropdown"
                  key="priceRange"
                  dict={dict}
                  value={filters.priceRange}
                  data={PRICE}
                  onChange={handleFilterChange}
                  name="priceRange"
                  placeholder="priceRange"
                  darkBg
                  disabled={loading}
                />
              </div>
            </APIProvider>
          </div>
        ) : (
          <div className="lg:mt-12 mt-8 mb-10">
            <CustomInputUnit
              handleUnitIdKeyDown={handleUnitIdKeyDown}
              placeholder={dict.unitInputPlaceholder}
              value={filters.unitId || ""}
              onChange={handleInputChange}
            />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-6 mt-auto">
          <AppIconButton
            bg={"dark"}
            iconOnRightSide
            icon={<Search size={16} />}
            intent={"primary"}
            label={dict.applyFilters}
            // disabled={isSearchDisabled()}
            className="gap-2"
            onClick={handleApplyFilters}
            disabled={loading}
          />
        </div>
      </div>
    </ContentLayout>
  );
};

export default SearchResultsHeader;
